import { ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useFetcher } from "react-router";
import { useAuthenticityToken } from "remix-utils/csrf/react";
import {} from "~/apis/favoriteCollege";
import { toast } from "~/components/molecules/Toast";
import { FavoritedCollegePayload, Messages } from "~/lib/sessions.server";
import { toast as notify } from "sonner";

export function ActionToastContainer({
    children,
    action,
}: {
    children: ReactNode;
    action?: {
        label: string;
        onClick: () => void;
    };
}) {
    return (
        <div className="flex flex-row items-center gap-2">
            {children}
            {action && (
                <button
                    className="inline-flex flex-none text-blue-80"
                    onClick={e => {
                        e.preventDefault();
                        action.onClick();
                    }}
                >
                    {action.label}
                </button>
            )}
        </div>
    );
}

function FavoritedCollegeContent({
    message: { collegeName, intent, collegeId },
}: {
    message: FavoritedCollegePayload;
}) {
    const csrf = useAuthenticityToken();
    const fetcher = useFetcher();
    const toastMessage = `${collegeName} was ${intent === "add" ? "added to" : "removed from"}`;
    const undoDelete = useCallback(() => {
        void fetcher.submit(
            {
                collegeId,
                intent: "add",
                csrf,
            },
            { method: "post", action: "/college-recruiting/favorite/college" },
        );
    }, [collegeId, fetcher, csrf]);
    const action = intent === "delete" ? { label: "Undo", onClick: undoDelete } : undefined;
    return (
        <ActionToastContainer action={action}>
            <div>
                {toastMessage}{" "}
                <Link className="text-blue-80" to="/college-recruiting/colleges/favorites">
                    My Colleges
                </Link>
                .
            </div>
        </ActionToastContainer>
    );
}
function GenericToastContent({ title, subtitle }: { title: string; subtitle?: string }) {
    return (
        <div className="flex flex-col gap-2">
            <div>{title}</div>
            {subtitle && <div className="font-metadata-medium">{subtitle}</div>}
        </div>
    );
}
export function useToastMessages(messages: Messages) {
    // use state to manage the latest toast for each message type
    const [state, setState] = useState<Record<string, string | number>>({});
    useEffect(() => {
        for (const message of messages) {
            switch (message.type) {
                case "favorite-college":
                    const key = `${message.type}-${message.collegeId}`;
                    if (state[key] !== undefined) {
                        notify.dismiss(state[key]);
                    }
                    const toastId = toast({
                        type: "success",
                        content: <FavoritedCollegeContent message={message} />,
                        closeButton: true,
                    });
                    setState(s => ({ ...s, [key]: toastId }));
                    break;
                case "info":
                case "success":
                case "error":
                case "warning":
                    toast({
                        type: message.type,
                        content: (
                            <GenericToastContent
                                title={message.title}
                                subtitle={message.subtitle}
                            />
                        ),
                        closeButton: true,
                    });
                    break;
                default:
                    break;
            }
        }
        // ignoring the state as a dependency as it constantly changes in the effects
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);
}
